import { v4 as uuidV4 } from 'uuid';
import { ColumnMapSelect, TraitValue, ColumnItem } from '../../../../types';
import { EventTrait, GroupedTraits, TraitDataType, TRAITS_DATA_TYPES } from '@/models/traits';
import { useAppSelector } from '@/reducers';
import { CONSENT_TRAIT, EVENT, TRAITS } from '../../../../constants';
import { DataSource, DataSourceFileConfig, Header } from '@/models/dataSource';

const SUPPORTED_CUSTOM_DATA_TYPES = new Set<string>(TRAITS_DATA_TYPES.map((dataType) => dataType.key));

/**
 * Maps backend customDataType onto the select options; unsupported values fall back to TEXT.
 * The API may propose BOOLEAN, but cdp-app-ingestion-csv cannot parse it yet, so it lands as TEXT.
 */
export const toCustomDataType = (customDataType?: string): TraitDataType => {
  if (customDataType && SUPPORTED_CUSTOM_DATA_TYPES.has(customDataType)) {
    return customDataType as TraitDataType;
  }

  return 'TEXT';
};

const canonicalize = (name: string): string => {
  return name.toLowerCase().replace(/ /gi, '').replace(/\./gi, '').replace(/_/gi, '').replace(/-/gi, '');
};

export const useColumnMapInitialValues = ({
  fileHeader,
  columnMapSelect,
  groupedTraits,
  defaultNonMatchingTraitId,
  suggestedHeaders,
  ignoreSavedHeaders = false,
}: {
  fileHeader: string[];
  columnMapSelect: ColumnMapSelect[];
  groupedTraits?: GroupedTraits;
  defaultNonMatchingTraitId?: number | string;
  suggestedHeaders?: Header[];
  ignoreSavedHeaders?: boolean;
}): ColumnItem[] => {
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;

  const traits = new Map<string, EventTrait>();

  const addTrait = (trait: EventTrait) => {
    const name = canonicalize(trait.dotName);
    traits.set(name, trait);

    if (trait.dotName.startsWith('address_')) {
      traits.set(name.substring(7), trait);
    }
  };

  if (groupedTraits) {
    groupedTraits.customer.forEach((trait) => addTrait(trait));

    if (dataSource?.configuration?.type === EVENT) {
      groupedTraits.event.forEach((trait) => addTrait(trait));
    }
  }

  const getTraitType = (headers: Header): ColumnMapSelect => {
    if (headers.custom) {
      return columnMapSelect[TRAITS.CUSTOM_TRAIT];
    }

    if (headers.traitId === CONSENT_TRAIT) {
      return columnMapSelect[TRAITS.CONSENT_TRAIT];
    }

    return columnMapSelect[TRAITS.MAP_TO_TRAIT];
  };

  const findTrait = (header: string): TraitValue | undefined => {
    const trait = traits.get(canonicalize(header));
    return trait
      ? {
          id: trait.id,
          displayValue: trait.displayName,
        }
      : undefined;
  };

  const savedHeaders = ignoreSavedHeaders ? undefined : dataSource?.configuration?.headers;
  const headersToApply = savedHeaders ?? suggestedHeaders;

  if (headersToApply) {
    return fileHeader.map((header, index) => {
      const id = uuidV4();
      const savedAtIndex = savedHeaders?.[index];
      const mappedHeader = savedHeaders ? (savedAtIndex?.name === header ? savedAtIndex : undefined) : suggestedHeaders?.[index];

      if (mappedHeader) {
        if (mappedHeader.skip) {
          return { id, header, trait: columnMapSelect[TRAITS.IGNORE_COLUMN] };
        }

        const traitType: ColumnMapSelect = getTraitType(mappedHeader);

        return {
          id,
          header,
          trait: traitType,
          traitValue: {
            id: mappedHeader.traitId || '',
            displayValue: mappedHeader.displayName || '',
          },
          ...(traitType.id === TRAITS.CUSTOM_TRAIT ? { customDataType: toCustomDataType(mappedHeader.customDataType) } : {}),
        };
      }

      return { id, header, trait: columnMapSelect[0], traitValue: undefined };
    });
  } else {
    return fileHeader.map((header) => {
      const id = uuidV4();
      const matchingTrait = findTrait(header);
      let trait = columnMapSelect[0];
      if (!matchingTrait && defaultNonMatchingTraitId) {
        trait = columnMapSelect.find((trait) => String(trait.id) === String(defaultNonMatchingTraitId)) || columnMapSelect[0];
      }
      return { id, header, trait, traitValue: matchingTrait };
    });
  }
};
